import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Add, Balance } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import PlatformService from '../../services/PlatformService';
import UrlValidationService from '../../services/UrlValidationService';
import { IPlatform } from '../../shared/interfaces/IPlatform';
import { ICompetitor } from '../../shared/interfaces/ICompetitor';
import PlatformSelect from '../PlatformSelect/PlatformSelect';
import BenchmarkingService from '../../services/BenchmarkingService';
import ScrapeService from '../../services/ScrapeService';
import CompanyNameService from '../../services/CompanyNameService';
import PlaceIDFinder from '../PlaceIDFinder/PlaceIDFinder';

interface Props {
  benchmarkID: string;
  subject: ICompetitor;
  competitors: ICompetitor[];
  companyUrl: string;
  label: string;
  id: string;
  platform: string;
  reload: () => Promise<void>;
}

const BenchmarkDropzone: FC<Props> = ({
  benchmarkID,
  competitors,
  id,
  platform,
  label,
  reload,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [competitorMonitoringLabel, setCompetitorMonitoringLabel] = useState('');
  const [competitorUrl, setCompetitorUrl] = useState('');
  const [isInvalidCompetitorUrl, setIsInvalidCompetitorUrl] = useState(false);
  const [isValidationInProgress, setIsValidationInProgress] = useState(false);
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [loading, setLoading] = useState(false);

  const addBenchmark = () => {
    setIsOpen(true);
  };

  const getCompetitorUrlLabel = (company: string) => {
    return isInvalidCompetitorUrl
      ? `${t('invalid_competitor_url')}`
      : `${t('company_url_label_competitor')} ` + platforms.find((v) => v.name === company)?.url;
  };

  const validateCompetitorUrl = async () => {
    if (platform.toLowerCase() === 'googleplace') {
      setIsInvalidCompetitorUrl(false);
      setIsValidationInProgress(false);
      setLoading(false);
      return true;
    }

    const isUrlValid = await UrlValidationService.validateUrl(competitorUrl);
    setLoading(true);

    if (isUrlValid) {
      setIsInvalidCompetitorUrl(false);
      setIsValidationInProgress(false);
      setLoading(false);
      return true;
    } else {
      setIsInvalidCompetitorUrl(true);
      setIsValidationInProgress(true);
      setLoading(false);
      return false;
    }
  };

  const handleCompetitorMonitoringLabelChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCompetitorMonitoringLabel(event.target.value);
  };

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async () => {
      const response = await PlatformService.getPlatforms(setLoading, controller.signal);

      if (!Array.isArray(response)) return;

      setPlatforms(response);
    };

    (async () => {
      await getPlatforms();
    })();

    return () => {
      controller.abort();
    };
  }, []);

  const handleAddBenchmark = async () => {
    try {
      setIsValidationInProgress(true);
      const isUrlValid = await validateCompetitorUrl();
      setIsValidationInProgress(false);

      if (!isUrlValid || !competitors || !benchmarkID) return false;

      if (!label) setIsValidationInProgress(false);

      const platformID = PlatformService.getPlatformId(platform.toLowerCase(), platforms);

      const companyName = await CompanyNameService.getCompanyName(platformID, competitorUrl);

      await ScrapeService.startScraping(
        platform,
        companyName,
        competitorMonitoringLabel,
        competitorUrl,
        true,
        null
      );

      await BenchmarkingService.addBenchmarkEntry(
        id,
        companyName,
        competitorMonitoringLabel,
        competitorUrl
      );

      await reload();
    } catch (error) {
      console.error('An error occurred while adding the benchmark:', error);
    } finally {
      setIsValidationInProgress(false);
    }
  };

  const addBenchmarkGuide = () => {
    if (isOpen) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: 400,
            length: 'auto',
            ml: theme.spacing(2),
            padding: theme.spacing(2),
          }}
        >
          <Typography sx={{ mt: theme.spacing(1), minWidth: '400px', textAlign: 'center' }}>
            {platform}
          </Typography>
          <TextField
            label={t('optional_label')}
            placeholder={`${t('example_company_placeholder')} (${platform})`}
            variant="standard"
            value={competitorMonitoringLabel}
            onChange={handleCompetitorMonitoringLabelChange}
            sx={{ mt: theme.spacing(1), minWidth: '400px' }}
          />
          {platform.toLowerCase() === 'googleplace' ? (
            <Box sx={{ mt: theme.spacing(1), maxWidth: '400px' }}>
              <PlaceIDFinder onChange={setCompetitorUrl} />
            </Box>
          ) : (
            <TextField
              label={getCompetitorUrlLabel(platform)}
              error={isInvalidCompetitorUrl}
              variant="standard"
              value={competitorUrl}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setCompetitorUrl(event.target.value);
                setIsInvalidCompetitorUrl(false);
              }}
              sx={{ mt: theme.spacing(1), minWidth: '400px' }}
            />
          )}
          {isValidationInProgress && (
            <Box>
              <LinearProgress sx={{ my: theme.spacing(1) }} />
            </Box>
          )}
          <Button
            disabled={
              !competitorUrl ||
              isValidationInProgress ||
              isInvalidCompetitorUrl ||
              !competitorMonitoringLabel
            }
            variant="contained"
            onClick={handleAddBenchmark}
            sx={{ mt: 1 }}
          >
            {loading ? (
              <CircularProgress
                sx={{ ml: theme.spacing(1) }}
                size={24}
              />
            ) : (
              t('add')
            )}
          </Button>
          <Button onClick={() => setIsOpen(false)}>{t('cancel')}</Button>
        </Box>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        borderRadius: theme.shape.borderRadius,
        border: `2px dashed ${theme.palette.divider}`,
        minWidth: 400,
        length: 'auto',
        ml: theme.spacing(2),
      }}
    >
      {!isOpen ? (
        <>
          <Box sx={{ display: 'flex' }}>
            <Balance />
          </Box>
          <Box sx={{ display: 'flex', mt: theme.spacing(1) }}>
            <Typography>{t('benchmarking_headline')}</Typography>
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography
              color={theme.palette.primary.main}
              variant={'caption'}
            >
              {t('benchmarking_text')}
            </Typography>
          </Box>
          <Box sx={{ display: 'none', mt: theme.spacing(2) }}>
            <Button
              disabled
              startIcon={<Add />}
            >
              {t('add_to_benchmark')}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', mt: theme.spacing(2) }}>
            <Button
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '50%',
                minWidth: 48,
                height: 48,
              }}
              onClick={addBenchmark}
            >
              <Add />
            </Button>
          </Box>
        </>
      ) : (
        addBenchmarkGuide()
      )}
    </Box>
  );
};

export default BenchmarkDropzone;
